import { useState, type FormEvent } from 'react'
import { apiPost } from '../../api/client'
import { userFacingMessage } from '../../api/errors'
import { useProfile } from '../../api/queries/profile'
import { Button } from '../../ui/Button'
import { TextArea } from '../../ui/TextArea'
import { TextField } from '../../ui/TextField'
import styles from './Settings.module.css'

/**
 * In-app feedback, restored where it was: a nav item, not a Settings card.
 *
 * It posts to the same endpoint as before — `POST /public/leads` with
 * `source: "in_app_feedback"` — so what arrives lands in the admin feedback
 * list exactly as it always has. Nothing new on the backend.
 */
export function FeedbackDialog({ onClose }: { onClose: () => void }) {
  const profile = useProfile()
  const [email, setEmail] = useState(profile.data?.email ?? '')
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setSending(true)
    setError(null)
    try {
      await apiPost('/public/leads', { email: email.trim(), message: message.trim(), source: 'in_app_feedback' })
      setSent(true)
    } catch (err) {
      setError(userFacingMessage(err))
    } finally {
      setSending(false)
    }
  }

  return (
    <div className={styles.overlay} role="presentation" onClick={onClose}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="feedback-title" className={styles.dialogTitle}>
          Send feedback
        </h2>

        {sent ? (
          <>
            <p className={styles.dialogBody}>Thanks — we have received it.</p>
            <div className={styles.actions}>
              <Button onClick={onClose}>Close</Button>
            </div>
          </>
        ) : (
          <form onSubmit={(e) => void submit(e)}>
            <p className={styles.dialogBody}>Tell us what is working, what is not, or what is missing.</p>
            <TextField
              label="Email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
            />
            <TextArea
              label="Feedback"
              rows={5}
              required
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What would you like us to know?"
            />
            {error ? (
              <p className={styles.error} role="alert">
                {error}
              </p>
            ) : null}
            <div className={styles.actions}>
              <Button type="submit" disabled={sending || !message.trim() || !email.trim()}>
                {sending ? 'Sending…' : 'Send feedback'}
              </Button>
              <Button type="button" onClick={onClose} disabled={sending}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
